import React from 'react';
import { FluentProvider, webLightTheme, webDarkTheme, Button } from '@fluentui/react-components';
import { ArrowClockwiseRegular, MailRegular } from '@fluentui/react-icons';
import { AccessMessage } from '../components/AccessMessage';
import { SupportEmail } from '../components/SupportEmail';
import { ACCESS_REQUEST_URL } from '../constants/support';
import InsufficientPermissions from '../components/icons/InsufficientPermissions';
import { usePrefersDarkTheme } from '../hooks/usePrefersDarkTheme';

interface AccessDeniedContentProps {
  onRetry?: () => void;
  /** Center on the full viewport, for use outside the app shell. */
  fullScreen?: boolean;
}

interface AccessDeniedProps {
  isDarkTheme?: boolean;
  onRetry?: () => void;
}

export const AccessDeniedContent: React.FC<AccessDeniedContentProps> = ({
  onRetry,
  fullScreen = false,
}) => (
  <AccessMessage
    fullScreen={fullScreen}
    icon={<InsufficientPermissions />}
    title="Access Denied"
    body="You don't currently have permission to access this page."
    supporting={
      <>
        If you believe you should have access, sign in with a different account, request
        access below or by emailing <SupportEmail />
      </>
    }
    actions={
      <>
        <Button
          as="a"
          href={ACCESS_REQUEST_URL}
          target="_blank"
          rel="noopener noreferrer"
          appearance="primary"
          icon={<MailRegular />}
        >
          Request Access
        </Button>
        {onRetry && (
          <Button appearance="secondary" onClick={onRetry}>
            Try Again
          </Button>
        )}
        <Button
          appearance="secondary"
          icon={<ArrowClockwiseRegular />}
          onClick={() => window.location.reload()}
        >
          Refresh Page
        </Button>
      </>
    }
  />
);

export const AccessDenied: React.FC<AccessDeniedProps> = ({ isDarkTheme, onRetry }) => {
  const prefersDark = usePrefersDarkTheme();
  const dark = isDarkTheme ?? prefersDark;

  return (
    <FluentProvider theme={dark ? webDarkTheme : webLightTheme}>
      <AccessDeniedContent fullScreen onRetry={onRetry} />
    </FluentProvider>
  );
};
